/**
 * BLOOM 앱의 한국어 사전. **키는 여기서 태어난다** — `en.ts` 가 이것을 따른다.
 *
 * **콘솔과 사전을 공유하지 않는다** (`docs/22` §3). 하나는 관리 도구이고 하나는 시청
 * 화면이라 문구가 겹치지 않는다 — 한 곳에 모으면 어느 키가 어느 화면 것인지 알 수
 * 없어지고 쓰지 않는 키가 쌓인다. 공유하는 것은 **언어 판정 규칙**(`@apm/i18n`) 하나다.
 *
 * **여기 없는 것**: 상품명·가격·채널명(등록된 값이다) · 진단 화면(`/debug`·`?debug=1`)과
 * `/sim/stb`(개발자와 데모 운영자만 본다 — D12).
 */
export const ko = {
  // ── 공통 ────────────────────────────────────────────────────────────
  'common.close': '닫기',
  'common.retry': '다시 시도',
  'common.loading': '준비 중…',
  'common.watchBroadcast': '방송 보기',
  'common.unknownError': '알 수 없는 오류',

  // ── 진입 ────────────────────────────────────────────────────────────
  // **채널 종류를 모르는 자리다** (절대 규칙 49). 게이트는 `start()` 앞에 그려지고
  // `channelKind` 는 `playlistLoaded` 에서야 오므로, 여기서 `kind` 로 가르면 일반
  // 채널에서도 언제나 홈쇼핑 문구다. 그래서 **한쪽을 고르지 않고 둘을 나란히** 말한다.
  //
  // **「개인화」라 쓰지 않는다** — v18 이 지웠고 지금 순서는 `Schedule.order` 하나다
  // (절대 규칙 48). **「로그인하면」을 빼지 않는다** — 비로그인은 이력이 없어(절대 규칙 46)
  // 조건을 지우는 순간 이 줄이 지키지 못할 약속이 된다.
  'gate.hint':
    '홈쇼핑 채널은 상품 영상이 하나의 채널처럼 이어지고, 드라마 채널은 로그인하면 보던 곳부터 이어서 봅니다.',
  'gate.start': '채널 시청 시작',
  'gate.hintResume': '판매처에서 돌아왔습니다. 보던 자리에서 이어집니다.',
  'gate.startResume': '이어서 보기',
  // v25 — 게이트에서 편성표를 연다. **문구가 중립이어야 한다**(절대 규칙 49):
  // 게이트는 `channelKind` 가 오기 **전에** 그려지므로 「회차」도 「상품」도 쓸 수 없다.
  'gate.openGuide': '방송편성 보기',

  // ── 홈 (v32 · `docs/63` §7) ──────────────────────────────────────────
  // 시작 게이트의 자리에 서는 화면. 회차·이어보기·종류 배지·LIVE·「지금」은 편성표의 키를
  // 다시 쓴다(`episodes.*` · `channels.kind*` · `onair.*` · `epg.entryPoint`).
  'home.nav.guide': '편성표',
  'home.rail.favorites': '즐겨찾기',
  'home.rail.channels': '채널',
  'home.rail.selling': '지금 판매 중',
  'home.startFromTop': '처음부터 보기',
  'home.shopFromTop': '이 상품 처음부터 보기',
  'home.next': '다음',
  'home.pressOk': 'OK 를 누르면 소리와 함께 재생됩니다',
  'watch.home': '홈',
  'settings.homePreview': '홈에서 미리보기',
  'settings.homePreviewOn': '켬',
  'settings.homePreviewOff': '끔',

  // ── 시청 화면 ───────────────────────────────────────────────────────
  'watch.loading': '불러오는 중…',
  'watch.settings': '설정',
  // v25 — 채널 재핑 ▲▼. **화살표는 그림이고 이름은 여기서 온다** (절대 규칙 24 —
  // 한글 훑기가 `aria-label` 도 본다). 문구가 중립이어야 하는 자리는 아니다 —
  // 컨트롤 바는 `channelKind` 를 안다.
  'watch.channelUp': '이전 채널',
  'watch.channelDown': '다음 채널',
  // 좌상단 시계 옆 스피커 버튼. **아이콘만 있어 `aria-label` 이 유일한 문구다** —
  // `verify-locale` 의 훑기가 텍스트 노드가 아니라 그 속성으로 이 버튼을 본다.
  'watch.mute': '소리 끄기',
  'watch.unmute': '소리 켜기',
  'watch.buyOnMobile': '모바일에서 구매',
  'watch.buyHere': '구매하기',
  'watch.requesting': '요청 중…',
  'watch.info': '정보보기',
  'watch.skip': '건너뛰기',
  'watch.skipIn': '건너뛰기 {n}초',
  'watch.allProducts': '전체 상품',
  'watch.allEpisodes': '전체 회차',
  // v39 콘텐츠 선택 (`docs/81` §5.1) — 고를 것이 둘 이상일 때만 「전체 회차」 대신 쓴다
  'watch.allContents': '콘텐츠',
  'contents.title': '콘텐츠',
  'contents.hint': '시리즈를 고르면 회차가 나옵니다. 편성에 없는 시리즈도 이 채널에서 바로 볼 수 있습니다.',
  'contents.episodeCount': '{n}편',
  'contents.watching': '시청 중',
  'contents.scheduled': '편성 중',
  'contents.library': '라이브러리',
  'contents.back': '콘텐츠',
  'contents.loadFailed': '라이브러리를 불러오지 못했습니다. 편성된 회차만 보입니다.',
  'contents.episodesFailed': '회차를 불러오지 못했습니다.',

  // v28 ONAIR (`docs/57` §1.3). **개인화 채널과 갈리는 문구 넷뿐이다** — 나머지 화면은
  // 같은 사전을 쓴다(음소거·설정·채널·편성표).
  'onair.live': '방송 중',
  'onair.pending': '아직 방송 전입니다',
  'onair.playbackFailed': '방송을 재생할 수 없습니다',
  // v32 실기(2026-09-10): 「LIVE」는 거짓말이다 — 서버가 잇는 채널이지 생방송이 아니다.
  // 2026-09-16: 「FAST」는 시장 카테고리라 시청자에게 뜻이 없다(이름 규칙 20 — 형용사로만
  // 쓴다). 홈 화면에서 이 배지가 가리키는 것은 **서버가 이어 주는 TV 같은 채널**이다.
  'onair.badge': 'TV',
  'epg.watching': '보는 중',
  'onair.past': '지나갔습니다',
  // v23. 상품 오버레이 (`docs/47` §9.5). **상품명·제휴사명은 여기 없다** — 그것은
  // 제휴사가 준 외부 데이터이고 화면이 `data-i18n="data"` 로 표시한다.
  'offer.badge': '이 장면의 상품',
  'offer.close': '닫기',
  // **모바일에서는 그 자리에서 산다** — 「모바일에서 구매」가 아니다.
  'offer.buy': '모바일에서 구매',
  'offer.buyHere': '구매하기',
  'watch.nextEpisode': '다음 회차',
  'watch.resumeToast': '{episode} {time}부터 이어봅니다',
  'watch.recordedLocked': '라이브 광고가 끝나면 다른 회차를 볼 수 있습니다',
  'episodes.title': '전체 회차',
  'episodes.hint': '고른 회차는 처음부터 재생됩니다.',
  'episodes.label': 'S{season} · {episode}회',
  'episodes.labelNoSeason': '{episode}회',
  // v18. 이어볼 회차 한 행에만 붙는다 — 회차별 진행률·완료 표시는 사라졌다
  'episodes.resumeAt': '{time}부터 이어보기',
  // 회차 길이. **콘텐츠 축이다** — 광고를 더해 세지 않는다 (절대 규칙 57).
  'episodes.duration': '{n}분',
  /*
   * 좌상단 시계 (v12). **문구 전체가 한 키다** — 언어마다 어순이 달라(`남음 A / B` vs
   * `A left / B`) 조각을 화면에서 이어 붙이면 그 순서가 사전 밖에 놓인다.
   *
   * 앞자리가 「남음」인 것은 숫자만 두면 대부분 「경과」로 읽기 때문이다.
   */
  'watch.remaining': '남음 {left} / {total}',
  // ── 광고 (v21) ────────────────────────────────────────────────────
  // **광고 중에만 쓰인다.** 정책이 `null` 인 채널에서는 한 줄도 그려지지 않는다.
  'watch.adRemaining': '광고 {left} 남음 / {total}',
  'watch.adPurchase': '이 광고 상품 보기',
  /*
   * v33. 라이브 이벤트 (절대 규칙 85). **경과도 남은 시간도 말하지 않는다** — 배지와
   * 이벤트 제목 둘뿐이고, 제목은 사람이 콘솔에 넣은 값이라 번역하지 않는다
   * (`data-i18n="data"` · 절대 규칙 24).
   */
  'watch.liveBadge': '🔴 LIVE',
  // v34-b. 컨트롤 바의 「라이브 ↔ 이어보기」 — 라이브 창이 열려 있는 동안만 선다
  'watch.toLive': '🔴 라이브 보기',
  'watch.toRecorded': '이어보기',
  /*
   * v35. 녹화물 탐색·정지 (절대 규칙 90). 라이브·광고 중에는 버튼이 없다. 「10초 후」가 컷 앞에서
   * 서는 것은 문구가 말하지 않는다 — 누르면 제자리일 뿐이다(`docs/71` §3).
   */
  // 아이콘 버튼의 `aria-label`·이름표다 — 기호는 아이콘이 그린다(낭독기가 기호를 읽지 않게)
  'watch.back10': '10초 전',
  'watch.forward10': '10초 후',
  'watch.pause': '일시정지',
  'watch.resume': '재생',
  /** 편성표의 라이브 상자. 이벤트 제목은 사람이 넣은 값이라 그 옆에 그대로 붙는다 */
  'epg.liveBadge': '🔴 LIVE',
  /**
   * v34 라이브 큐 — 상자가 말하는 큐 상태 셋(`docs/69` §7.2 · 도 17). 「예상 종료」는 **예상**이다 —
   * 부조정실이 종료를 누를 때까지 늘어날 수 있다. 남은 시간을 세지 않는다.
   */
  'epg.live.waiting': '개시 대기',
  'epg.live.extended': '연장 중',
  'epg.live.expectedEnd': '예상 종료 {time}',
  'watch.adSkip': '광고 건너뛰기',
  'watch.adSkipIn': '{n}초 후 건너뛰기',
  'watch.adBadge': '광고',
  // 「전체 회차」 시트의 배지. **두 값으로 그린다** — 더해서 하나로 보이면 이어보기가
  // 그 값을 쓴다고 오해할 수 있다 (절대 규칙 57 · D18).
  'watch.adLoad': '+광고 {n}분',

  // ── 선택 구매 (v11) ─────────────────────────────────────────────────
  // **「전체 상품」은 그 채널의 편성 전부다** (절대 규칙 34). 채널 밖으로 나가지 않으므로
  // 「하지 말 것」의 「전체 상품 목록 화면」 금지와 겹치는 것은 문구뿐이다 (`docs/27` §6).
  'products.title': '전체 상품',
  'products.hint': '고른 상품이 바로 재생되고, 이어서 구매할 수 있습니다.',
  // 지금 재생 중인 줄에 붙는다. `aria-current` 와 같은 자리이고, **글자로도 알 수 있어야
  // 한다** — 리모컨으로 3m 떨어져 보면 테두리만으로는 갈리지 않는다.
  'products.nowPlaying': '지금 재생 중',

  // ── 핸드오프 오버레이 ───────────────────────────────────────────────
  // 복수형 두 키다. 한국어는 같은 문장이지만 **영어가 「1 phone / 2 phones」로 갈린다** —
  // 「phone(s)」 같은 표기를 화면에 내보내지 않으려면 이 자리가 필요하다 (`docs/22` §4.3).
  'handoff.pushSent_one': '휴대폰 {n}대로 알림을 보냈습니다',
  'handoff.pushSent_other': '휴대폰 {n}대로 알림을 보냈습니다',
  'handoff.pushSentHint': '휴대폰에서 알림을 눌러 계속 구매하세요.',
  // **「울리지 않으면」이 아니다.** 무음·진동에서는 울리지도 않고, 시청자가 기다리는
  // 것은 소리가 아니라 알림이다 — 영어(`doesn't receive a notification`)와 같은 자리다.
  'handoff.pushSentWithQrHint': '휴대폰에 알림이 오지 않으면 오른쪽 QR 을 스캔하세요.',
  'handoff.qrIfSilent': '휴대폰에 알림이 오지 않으면',
  'handoff.failed': '휴대폰으로 넘기지 못했습니다',
  'handoff.failedQrHint': '{reason} · QR 로 구매하세요',
  'qr.scanHint': '휴대폰으로 스캔하세요',
  'qr.alt': '구매 페이지 QR 코드',

  // **어떻게 찾았는가** (`docs/21` W7). 시청자에게 하는 말이라 내부 이름
  // (`account_matched`)을 그대로 쓰지 않는다 — 그쪽은 `?debug=1` 패널의 것이다.
  'handoff.matchedByAccount': '계정으로 찾았습니다',
  'handoff.matchedByNetwork': '같은 네트워크에서 찾았습니다',

  // 실패 사유. **종류마다 시청자가 할 일이 다르다** — 그래서 `tv2m.ts` 가 실패를
  // `TriggerError` 로 이름 붙여 올린다.
  'handoff.reason.timeout': '응답이 늦습니다',
  'handoff.reason.network': '연결하지 못했습니다',
  'handoff.reason.status': '요청이 처리되지 않았습니다',
  'handoff.reason.noPhone': '알림을 받을 휴대폰을 찾지 못했습니다',
  'handoff.reason.noQr': 'QR 코드를 받지 못했습니다',
  'handoff.reason.notAvailable': '지금은 휴대폰으로 넘길 수 없습니다',
  'handoff.reason.unknown': '알 수 없는 오류입니다',

  // 시트가 열려 있는데 서버가 다른 tier 를 골랐을 때의 한 줄. **숨기지 않는다.**
  'handoff.note.pushedOthers_one': '다른 휴대폰 {n}대에도 알림을 보냈습니다',
  'handoff.note.pushedOthers_other': '다른 휴대폰 {n}대에도 알림을 보냈습니다',
  'handoff.note.serverChoseQr': '서버가 QR 경로를 골랐습니다',
  'handoff.note.notRecorded': '구매 기록을 남기지 못했습니다',

  // ── 채널 목록 (v9) ──────────────────────────────────────────────────
  // ── 채널 편성표 (v25, `docs/51`) ──────────────────────────────
  //
  // `channels.title`·`channels.hint` 를 **지웠다** — 쓰던 두 화면이 편성표로 대체됐다.
  // `channels.change` 는 컨트롤 바·설정이 계속 쓰므로 남는다.
  //
  // ★ **종류로 갈리는 키를 새로 만들지 않는다.** 갈리는 넷이 전부 이미 있다 —
  // `channels.kind*` · `itemCount`/`episodeCount` · `episodes.label`/`meta.category` ·
  // `formatPrice`/`episodes.duration`+`watch.adLoad`. 제목·안내는 **중립이어야 한다**
  // (절대 규칙 49) — 홈쇼핑과 일반이 한 화면에 섞이므로 한쪽을 가정할 수 없다.
  'epg.title': '채널 편성표',
  'epg.hint': '고른 것부터 바로 재생됩니다.',
  'epg.favorites': '즐겨찾는 채널',
  'epg.allChannels': '모든 채널',
  'epg.emptyFavorites': '채널 이름 옆의 별을 누르면 여기에 모입니다.',
  'epg.favorite': '즐겨찾기',
  // v26 — 번호 배지의 읽히는 이름. **숫자 자체는 데이터라 번역하지 않는다**(절대 규칙 24 —
  // `data-i18n="data"`). 화면에는 숫자만 보이고 이 문장은 스크린 리더가 읽는다.
  'epg.channelNumber': '채널 {n}번',
  // 「지금」과 「지금 재생 중」은 다른 것이다 — 앞은 「이 채널을 켜면 나올 것」(안 보고
  // 있는 채널), 뒤는 「지금 이 화면에서 재생 중」(보고 있는 채널 한 곳). 합치면 편성표를
  // 열 때마다 모든 채널이 「지금 재생 중」이라고 말한다.
  'epg.entryPoint': '지금',
  'epg.nowPlaying': '지금 재생 중',
  // ★ **약속이 아니라 어림이다.** 스킵·선택이 일어나면 즉시 틀어지므로 「약」이 붙고
  // **분 단위로만** 말한다 — 초까지 쓰면 정확해 보이고, 정확해 보이면 시청자가 믿는다
  // (절대 규칙 46 의 「근거 없는 표시는 틀린 것보다 나쁘다」가 이 자리에도 걸린다).
  'epg.startsInAbout': '약 {n}분 후',
  // **일반 채널이 하나라도 있을 때만** 그린다 — 홈쇼핑만 있는 배포에서는 이어보기가
  // 원리적으로 없어 이 문장이 거짓이다.
  'epg.signedOutHint': '로그인하면 드라마 채널을 보던 회차부터 이어집니다.',
  'epg.scrollHint': '← → 로 더 볼 수 있습니다',
  // ── v27 시각축 편성표 (`docs/55` · 절대 규칙 73) ──────────────────────────
  // 안내가 **읽는 법**을 말한다 — 가로가 시간이라는 것을 모르면 이 화면은 그냥 목록이다.
  'epg.gridHint': '가로는 시간, 세로는 채널입니다. 상자를 고르면 바로 재생됩니다.',
  // 「지금」 선의 칩. `epg.entryPoint` 와 글자는 같지만 **뜻이 다르다** — 저것은 상자
  // 하나가 「이 채널을 켜면 나올 것」이라는 말이고, 이것은 **축 위의 한 점**이다.
  // 합치면 둘 중 하나를 고칠 때 다른 하나가 조용히 따라 바뀐다.
  'epg.nowMarker': '지금',
  // 스크롤해 앞을 보다가 되돌아오는 버튼. **언제나 그린다** — 스크롤했을 때만 그리면
  // 그 버튼 위에서 누르는 순간 버튼이 사라져 포커스가 `<body>` 로 떨어진다 (D15).
  'epg.backToNow': '지금으로',
  'epg.adsLabel': '광고 시간',
  'epg.adsOn': '켬',
  'epg.adsOff': '끔',
  'epg.timeAxis': '시간축',
  // ▒ 의 보이지 않는 이름. 화면에서는 색으로만 갈리므로 읽히는 이름이 있어야 한다.
  'epg.seenPart': '이미 본 구간',
  // 되돌아온 바퀴(`cycle ≥ 1`)의 상자. **고르면 처음부터 재생된다** — 그것이 참이고,
  // 시청자는 「지금 나올 것」이 아니라 「한 바퀴 뒤의 그것」을 가리킨 것이다.
  'epg.repeats': '다시 처음부터',
  // **치환 이름이 `{n}` 인 것이 계약이다.** 복수형을 고르는 것은 `params.n` 하나이고
  // (`createTranslator`), 다른 이름으로 부르면 **키가 화면에 그대로 나온다** — 타입은
  // 이것을 잡지 못한다(C7 실기에서 `{count}` 로 불러 그렇게 됐다). 절대 규칙 24 가
  // 덮는 것은 키의 유무이지 치환 이름이 아니다.
  //
  // **두 줄이 같은 한국어인 것이 정상이다** (`docs/22` §4.3). 한국어는 수에 따라 형태가
  // 바뀌지 않지만, `_one` 을 여기 두지 않으면 `MessageKey` 에 그 키가 없어 **영어가
  // 단수형을 가질 수 없다.** 원본이 키를 낳는다는 규칙의 대가이고, 그 덕에 번역 누락
  // 검사가 사전 하나로 성립한다 (절대 규칙 24).
  'channels.kindHomeshopping': '홈쇼핑',
  // v33-g. **일반 채널의 배지는 종류가 아니라 장르다**(`channel-badge.ts`). 장르가 없으면 배지가 없다.
  'channels.genre.drama': '드라마',
  'channels.genre.movie': '영화',
  'channels.genre.sports': '스포츠',
  'channels.genre.news': '뉴스',
  'channels.genre.entertainment': '예능',
  'channels.genre.documentary': '다큐',
  'channels.itemCount_one': '상품 {n}개',
  'channels.itemCount_other': '상품 {n}개',
  'channels.episodeCount_one': '회차 {n}개',
  'channels.episodeCount_other': '회차 {n}개',
  'channels.emptyTitle': '방송 중인 채널이 없습니다',
  // 두 종류를 함께 나열하는 화면이라 이 문구도 중립이다 (절대 규칙 49).
  'channels.emptyText':
    '아직 어느 채널에도 재생할 수 있는 편성이 없습니다. 관리자 콘솔에서 편성을 마친 뒤 다시 열어 주세요.',
  'channels.change': '채널 바꾸기',

  // ── 유입 배지 ───────────────────────────────────────────────────────
  // 유입 트리거의 제목. **한국어는 `TV2M_NOTIFICATION_TITLES` 와 같은 문자열이어야
  // 한다** — 알림에 뜬 것과 화면에 뜬 것이 다르면 다른 상품에 온 줄 안다.
  // `TriggerBadge.test.ts` 가 그 일치를 검사한다 (절대 규칙 10).
  'badge.triggerAd': '📺 TV에서 본 상품',
  'badge.triggerChannelContent': '📺 방송 중인 상품',
  // v23. **`TV2M_NOTIFICATION_TITLES.content_offer` 와 같은 문자열이어야 한다** —
  // `TriggerBadge.test.ts` 가 그 일치를 붙든다 (절대 규칙 24 의 그 문단).
  'badge.triggerContentOffer': '🛍 방금 그 장면의 상품',
  'badge.tv2m': '알림에서 열림',
  'badge.tv2mSameDevice': '같은 기기에서 이어보기',
  'badge.tv2mDeferred': 'QR 설치 후 복원',
  'badge.tv2mSameDeviceDeferred': '설치 후 복원',

  // ── 상품 화면·시트 ──────────────────────────────────────────────────
  'product.title': '상품',
  'product.loadingTitle': '상품 정보를 불러오는 중',
  'product.continueWatching': '계속 보기',
  'product.coupon': '쿠폰 {code} 적용 대상',
  'product.loadFailed':
    '상품 정보를 가져오지 못했다. 방송 준비 중이거나 상품 서버에 닿지 못한 상태다.',
  'product.creativeLoadFailed':
    '광고 소재를 가져오지 못했다. 캠페인이 끝났거나 광고 서버에 닿지 못한 상태다.',
  'product.noDeepLink': '판매처 주소를 확인하지 못했습니다.',
  'product.goToStore': '판매처로 이동',
  'product.leaving': '판매처로 이동 중…',
  'product.openedInNewTab': '새 탭에서 판매처를 열었습니다.',
  'product.handedOffToStore': '판매처로 나갑니다. 돌아오면 보던 자리에서 이어집니다.',
  'product.purchaseDone': '구매 완료 (데모)',
  'product.idLabel': '상품 id',
  'product.missingTitle': '상품 정보가 없습니다',
  'product.missingText':
    '링크가 만료됐거나 이 기기에 저장된 상품이 없습니다. 방송을 보다가 [모바일에서 구매] 를 다시 눌러 주세요.',

  // ── 설정 시트 ───────────────────────────────────────────────────────
  'settings.title': '설정',
  'settings.language': '언어',
  // v21. **`adPolicy !== null` 일 때만 렌더된다** — 홈쇼핑 채널에는 줄 자체가 없다
  // (절대 규칙 56). 없는 것을 끌 수 있게 보이면 그것이 곧 「홈쇼핑에도 광고가 있다」는 신호다.
  'settings.ads': '광고',
  'settings.adsOn': '켬',
  'settings.adsOff': '끔',
  'settings.adsHint':
    '데모용입니다. 끄면 다음 브레이크부터 광고가 나오지 않고, 재생은 멈추지 않습니다.',
  // v23. **`offerPolicy !== null` 일 때만 렌더된다** — 홈쇼핑 채널에는 줄 자체가 없다
  // (절대 규칙 66). 광고 줄과 **따로** 켜고 끈다: 광고만 끈 채 상품을 보이는 것이
  // 데모의 한 장면이다.
  'settings.offers': '상품 추천',
  'settings.offersOn': '켬',
  'settings.offersOff': '끔',
  'settings.offersHint':
    '데모용입니다. 이 기기에서만 적용되고, 지금 떠 있는 상품은 그대로 유지됩니다.',
  'settings.preparing': '준비 중',
  'settings.householdFailed': '가구 등록 실패 — 서버에 닿지 못했다',
  'settings.householdOk': '가구에 등록됨',
  'settings.resumeLastProduct': '마지막으로 본 상품 이어서 보기',
  'settings.openDiagnostics': '진단 화면 열기',
  'settings.notLoggedIn': '로그인하지 않음 — 가구(IP)로만 매칭됩니다',
  'settings.deviceLinked': '이 기기가 계정에 묶였습니다',
  'settings.deviceNotLinked': '계정에 묶이지 않았습니다 — 계정 매칭이 안 됩니다',
  'settings.signOut': '로그아웃',
  'settings.signingOut': '로그아웃 중…',
  'settings.signInBenefit':
    '로그인하면 같은 계정의 다른 기기로도 알림이 갑니다 — 망이 달라도 이어집니다.',

  // ── 알림 권한 ───────────────────────────────────────────────────────
  'push.iosTitle': '알림을 받으려면 홈 화면에 추가하세요',
  'push.iosText': 'iPhone·iPad 는 홈 화면에 추가한 뒤에만 알림을 받을 수 있습니다.',
  'push.iosStep1': 'Safari 아래쪽 공유 버튼(□↑)을 누릅니다.',
  'push.iosStep2': '[홈 화면에 추가] 를 선택합니다.',
  'push.iosStep3': '홈 화면의 아이콘으로 다시 열어 알림을 켭니다.',
  'push.on': '알림 켜짐',
  'push.enableTitle': '알림을 켜 주세요',
  'push.enableText':
    'TV 에서 상품을 고르면 이 휴대폰으로 바로 알려 드립니다. 알림이 없으면 QR 을 다시 찍어야 합니다.',
  'push.enable': '알림 켜기',
  'push.requesting': '요청 중…',
  'push.subscribed': '알림이 켜졌습니다.',
  'push.denied': '알림이 거부된 상태입니다. 브라우저의 사이트 설정에서 알림을 허용해 주세요.',
  'push.noVapid': '서버에 알림 키(VAPID)가 설정되지 않았습니다. 관리자에게 알려 주세요.',
  'push.unsupported': '이 브라우저는 웹 알림을 지원하지 않습니다.',
  'push.subscribeFailed': '알림 구독을 만들지 못했습니다. 잠시 후 다시 시도해 주세요.',
  'push.keyMismatch': '알림 키가 서버와 어긋났습니다. 앱을 다시 열면 새 키로 구독을 만듭니다.',
  'push.modeMismatch': '이 배포의 알림 설정이 앱과 어긋났습니다. 관리자에게 알려 주세요.',
  'push.registerFailed': '구독을 서버에 등록하지 못했습니다. 잠시 후 다시 시도해 주세요.',
  'push.notAsked': '알림 권한을 아직 묻지 않았습니다.',

  // ── 로그인 ──────────────────────────────────────────────────────────
  'login.title': 'BLOOM 을 시작하려면 로그인하세요',
  'login.text': '구글 계정 하나면 됩니다. 별도 가입 절차는 없습니다.',
  'login.benefit':
    '같은 계정으로 로그인한 TV 와 휴대폰이 이어집니다 — TV 에서 상품을 고르면 이 계정의 휴대폰으로 바로 알려 드립니다.',
  'login.noClientId':
    '이 서버에 구글 클라이언트 ID 가 설정되지 않았습니다. 관리자에게 알려 주세요 (',
  'login.alreadySignedIn': '이미 로그인되어 있습니다',
  // v17. 셸은 GIS 를 쓸 수 없어(구글이 임베디드 WebView 를 막는다) 우리 버튼을 그린다
  'login.nativeButton': '구글로 로그인',
  'login.nativeSigningIn': '로그인 중…',
  'login.nativeFailed': '로그인을 마치지 못했습니다. 다시 시도해 주세요.',
  // 셸이 낡아 로그인 통로가 없다. **할 일이 재설치 하나로 정해지는 문구여야 한다**
  'login.shellTooOld': '이 버전의 앱에서는 로그인할 수 없습니다. 앱을 새로 설치해 주세요.',
  'login.callbackFailed': '로그인을 마치지 못했습니다. 다시 시도해 주세요.',

  // ── 설치 안내 ───────────────────────────────────────────────────────
  'install.title': '홈 화면에 추가하고 상품을 확인하세요',
  'install.text':
    'TV 에서 고른 상품이 준비돼 있습니다. 홈 화면에 추가하면 다음부터 알림으로 바로 받아 볼 수 있습니다.',
  'install.step1': '브라우저 메뉴에서 [홈 화면에 추가] 를 누릅니다.',
  'install.step2': '추가된 아이콘으로 앱을 엽니다.',
  'install.step3': '고른 상품이 자동으로 열립니다.',
  'install.notRestoredTitle': '상품을 복원하지 못했습니다',
  'install.notRestoredText':
    'QR 코드는 표시된 후 잠시 동안만 유효합니다. TV 화면에서 [모바일에서 구매] 를 다시 눌러 새 QR 코드를 받아 주세요.',
  'install.checkNow': '고른 상품 지금 확인',
  'install.checking': '확인 중…',

  // ── 완료 화면 ───────────────────────────────────────────────────────
  'done.title': '완료 화면 (데모)',
  'done.text':
    '실제 결제는 판매처에서 일어납니다. 이 화면은 TV 에서 시작한 전환이 어디까지 이어졌는지를 시연에서 보여 주기 위한 대역입니다.',
  'done.recorded': 'purchase_completed 기록됨',
  'done.home': '홈으로',

  // ── 부팅 실패 ───────────────────────────────────────────────────────
  'boot.configFailed': '설정을 받지 못했습니다',
  'boot.server': '서버: {url}',
} as const;

export type MessageKey = keyof typeof ko;
